/**
 * Warm-up and Phase Control for MediaPipe Smoothing
 * Handles initial frame stabilization and dynamic parameter adjustment
 */

export interface WarmupCfg { 
  frames: number;    // Number of frames to warm up
  rampMs: number;    // Milliseconds to ramp smoothing parameters
}

export type Phase = 'warmup' | 'still' | 'motion' | 'endcap';

export interface PhaseInfo {
  phase: Phase;
  frameCount: number;
  elapsedMs: number;
  rampFactor: number; // 1.0 at start → 0.0 after ramp
}

export class PhaseController {
  private frameCount = 0;
  private t0?: number;
  private lastMediaTime?: number;
  private sessionStartTime?: number;

  constructor(private warmup: WarmupCfg) {}

  step(mediaTimeS: number, isStill: boolean, isEndcap = false): PhaseInfo {
    // Initialize timing on first frame
    if (this.t0 === undefined) {
      this.t0 = performance.now();
      this.sessionStartTime = mediaTimeS;
    }

    this.frameCount++;
    const elapsedMs = performance.now() - this.t0;
    const rampFactor = this.calculateRampFactor(elapsedMs);

    // Determine phase
    let phase: Phase;
    if (this.frameCount <= this.warmup.frames || elapsedMs <= this.warmup.rampMs) {
      phase = 'warmup';
    } else if (isEndcap) {
      phase = 'endcap';
    } else {
      phase = isStill ? 'still' : 'motion';
    }

    this.lastMediaTime = mediaTimeS;

    return {
      phase,
      frameCount: this.frameCount,
      elapsedMs,
      rampFactor
    };
  }

  private calculateRampFactor(elapsedMs: number): number {
    // Returns 1.0 at start → 0.0 after ramp completion
    if (elapsedMs <= 0) return 1.0;
    const progress = elapsedMs / this.warmup.rampMs;
    return Math.max(0, 1 - Math.min(1, progress));
  }

  reset(): void {
    this.frameCount = 0;
    this.t0 = undefined;
    this.lastMediaTime = undefined;
    this.sessionStartTime = undefined;
    console.log('[PhaseController] Reset for new session');
  }

  getStats(): {
    frameCount: number;
    phase: Phase;
    rampFactor: number;
    sessionDuration: number;
  } {
    const elapsedMs = this.t0 ? performance.now() - this.t0 : 0;
    const sessionDuration = this.lastMediaTime && this.sessionStartTime ? 
      this.lastMediaTime - this.sessionStartTime : 0;

    return {
      frameCount: this.frameCount,
      phase: this.frameCount <= this.warmup.frames ? 'warmup' : 'motion',
      rampFactor: this.calculateRampFactor(elapsedMs),
      sessionDuration
    };
  }
}

/**
 * Calculate dynamic smoothing parameters based on phase and motion state
 */
export function calculateDynamicParams(
  baseParams: { minCutoff: number; beta: number; dCutoff: number },
  phaseInfo: PhaseInfo,
  isStill: boolean,
  stillnessDeltas: { minCutoff: number; beta: number }
): { minCutoff: number; beta: number; dCutoff: number } {
  const params = { ...baseParams };

  // Apply warm-up ramping (stronger smoothing initially)
  if (phaseInfo.phase === 'warmup') {
    const ramp = phaseInfo.rampFactor;
    params.minCutoff *= (1 - ramp * 0.5); // Reduce cutoff during warmup (more smoothing)
    params.beta *= (1 - ramp * 0.3);      // Reduce responsiveness during warmup
  }

  // Apply stillness adjustments (tighter smoothing when still)
  if (isStill) {
    params.minCutoff += stillnessDeltas.minCutoff;
    params.beta += stillnessDeltas.beta;
  }

  // Clamp parameters to reasonable ranges
  params.minCutoff = Math.max(0.1, Math.min(5.0, params.minCutoff));
  params.beta = Math.max(0.001, Math.min(0.1, params.beta));

  return params;
}

/**
 * Detect if we're near the end of a sequence (for endcap processing)
 */
export function isEndcap(
  currentTime: number,
  totalDuration: number,
  endcapWindowS: number = 0.5
): boolean {
  return (totalDuration - currentTime) <= endcapWindowS;
}

/**
 * Apply per-joint smoothing multipliers for problem areas
 */
export function applyPerJointMultipliers(
  params: { minCutoff: number; beta: number; dCutoff: number },
  jointIndex: number,
  multipliers: { wristMult?: number; ankleMult?: number }
): { minCutoff: number; beta: number; dCutoff: number } {
  const isWrist = jointIndex === POSE_IDX.leftWrist || jointIndex === POSE_IDX.rightWrist;
  const isAnkle = jointIndex === POSE_IDX.leftAnkle || jointIndex === POSE_IDX.rightAnkle;

  const adjustedParams = { ...params };

  if (isWrist && multipliers.wristMult) {
    adjustedParams.minCutoff *= multipliers.wristMult;
    adjustedParams.beta *= multipliers.wristMult;
  }

  if (isAnkle && multipliers.ankleMult) {
    adjustedParams.minCutoff *= multipliers.ankleMult;
    adjustedParams.beta *= multipliers.ankleMult;
  }

  return adjustedParams;
}
